import { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import useSectionReveal from '@/hooks/useSectionReveal';
import { faqs } from '@/mocks/sections';

interface ItemProps {
  q: string;
  a: string;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

function FaqItem({ q, a, index, isOpen, onToggle }: ItemProps) {
  const panel = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLParagraphElement>(null);
  const first = useRef(true);

  useLayoutEffect(() => {
    const el = panel.current;
    const txt = inner.current;
    if (!el || !txt) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (first.current || reduce) {
      first.current = false;
      gsap.set(el, { height: isOpen ? 'auto' : 0 });
      gsap.set(txt, { y: 0, opacity: 1 });
      return;
    }
    if (isOpen) {
      gsap.to(el, { height: 'auto', duration: 0.5, ease: 'power2.inOut', overwrite: true });
      gsap.fromTo(txt, { y: 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.45, delay: 0.1, ease: 'power3.out', overwrite: true });
    } else {
      gsap.to(el, { height: 0, duration: 0.42, ease: 'power2.inOut', overwrite: true });
      gsap.to(txt, { opacity: 0, y: -4, duration: 0.25, ease: 'power2.out', overwrite: true });
    }
  }, [isOpen]);

  const id = `faq-panel-${index}`;
  return (
    <div data-reveal className={`border-b transition-colors duration-500 ${isOpen ? 'border-primary-400/70' : 'border-background-300/70'}`}>
      <button type="button" id={`${id}-btn`} onClick={onToggle} aria-expanded={isOpen} aria-controls={id} className="group w-full flex items-center justify-between gap-6 py-6 text-left cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-500">
        <span className={`font-heading text-base md:text-xl transition-colors duration-300 ${isOpen ? 'text-primary-900' : 'text-foreground-950 group-hover:text-primary-800'}`}>{q}</span>
        <span aria-hidden="true" className="relative w-6 h-6 flex items-center justify-center shrink-0 text-primary-700">
          <span className="absolute w-3.5 h-px bg-current" />
          <span className={`absolute w-3.5 h-px bg-current transition-transform duration-500 ease-[cubic-bezier(.65,0,.35,1)] ${isOpen ? 'rotate-0' : 'rotate-90'}`} />
        </span>
      </button>
      <div ref={panel} id={id} role="region" aria-labelledby={`${id}-btn`} className="overflow-hidden h-0">
        <p ref={inner} className="pb-6 text-sm text-foreground-600 leading-relaxed max-w-2xl">{a}</p>
      </div>
    </div>
  );
}

export default function FaqSection() {
  const ref = useRef<HTMLElement>(null);
  useSectionReveal(ref);
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section ref={ref} id="faq" className="bg-background-100 px-4 md:px-10 py-20 md:py-32">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <p data-reveal className="text-[11px] uppercase tracking-[0.24em] text-primary-600">FAQ</p>
          <h2 data-reveal className="mt-4 font-heading font-light text-3xl md:text-5xl leading-[1.1] text-foreground-950">Questions homeowners ask.</h2>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 border-t border-background-300/70">
          {faqs.map((f, i) => (
            <FaqItem key={f.q} q={f.q} a={f.a} index={i} isOpen={open === i} onToggle={() => setOpen((cur) => (cur === i ? null : i))} />
          ))}
        </div>
      </div>
    </section>
  );
}